import VueI18n from 'vue-i18n';
import Vue from 'vue';
import lang_ru from '@/lang/ru.json';
import lang_en from '@/lang/en.json';
import lang_es from '@/lang/es.json';
import lang_de from '@/lang/de.json';

export type SupportedLocale = 'ru' | 'en' | 'es' | 'de';

export interface LocaleOption {
  code: SupportedLocale;
  shortLabel: string;
  nativeName: string;
}

export const SUPPORTED_LOCALES: LocaleOption[] = [
  { code: 'ru', shortLabel: 'RU', nativeName: 'Русский' },
  { code: 'en', shortLabel: 'EN', nativeName: 'English' },
  { code: 'de', shortLabel: 'DE', nativeName: 'Deutsch' },
  { code: 'es', shortLabel: 'ES', nativeName: 'Español' },
];

export const DEFAULT_LOCALE: SupportedLocale = 'ru';
const LOCALE_STORAGE_KEY = 'locale';

function isValidLocale(value: string | null): value is SupportedLocale {
  return SUPPORTED_LOCALES.some(l => l.code === value);
}

function detectBrowserLocale(): SupportedLocale {
  const languages = navigator.languages ? navigator.languages : [navigator.language];

  for (const lang of languages) {
    const code = lang.split('-')[0].toLowerCase();

    if (isValidLocale(code)) {
      return code;
    }
  }

  return DEFAULT_LOCALE;
}

function getInitialLocale(): SupportedLocale {
  const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
  if (isValidLocale(saved)) {
    return saved;
  }
  const detected = detectBrowserLocale();
  persistLocale(detected);
  return detected;
}

function persistLocale(locale: SupportedLocale): void {
  localStorage.setItem(LOCALE_STORAGE_KEY, locale);
}

// Keep <html lang> in sync with the active UI locale. A correct lang reduces the
// browser's "translate this page?" prompt (the only available lever for Safari) and
// improves accessibility; it is orthogonal to translate="no" (which forbids translation).
function syncHtmlLang(locale: SupportedLocale): void {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.lang = locale;
  }
}

Vue.use(VueI18n);

export const i18n = new VueI18n({
  locale: getInitialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  silentFallbackWarn: true,
  messages: {
    ru: lang_ru,
    en: lang_en,
    es: lang_es,
    de: lang_de,
  },
});

// Reflect the initial (persisted/detected) locale on load.
syncHtmlLang(getLocale());

export function setLocale(locale: SupportedLocale): void {
  i18n.locale = locale;
  persistLocale(locale);
  syncHtmlLang(locale);
}

export function getLocale(): SupportedLocale {
  return i18n.locale as SupportedLocale;
}

